import { z } from 'zod';

/**
 * ATC 执行页的对外契约。
 *
 * **只承载 ADR-0004 允许的三类动作**：看结果、做审批、触发任务。
 * 契约里没有任何「推进到下一步」「提交表单」的形状——那两类界面被禁
 * （ADR-0005、ADR-0006、B-1），而形状不存在就写不出来。
 */

export const execAtcSummarySchema = z.object({
  code: z.string(),
  name: z.string(),
  ownerScope: z.string(),
  status: z.string(),
  maturityLevel: z.string(),
  positionName: z.string().nullable(),
  runCount: z.number(),
  lastRunAt: z.string().nullable(),

  /* ═══════ 以下六项为 C-160 A2「大卡片平面图」所加 ═══════
   *
   * 每一项都**先核了库再加**，逐项对应口径一的三态之一（回报里有那张表）：
   *
   * | 项 | 源 | 态 |
   * |---|---|---|
   * | `hotlGate` | `atc_version.hotl_gate`（boolean，存在） | 真值；无版本时 null |
   * | `triggerType` | `atc_version.trigger_type`（CHECK 四值，存在） | 真值；无版本时 null |
   * | `oneLiner` / `oneLinerMissing` | 视图 `v_atc_one_liner`（存在） | 有 action_spec 则真值，否则零态 + 缺什么 |
   * | `avgMinutes` | 视图 `v_atc_avg_duration`（存在，源 taskrun） | 零 taskrun 则 null＝零态，**不是无源** |
   * | `actorMix` | `taskrun.actor_type` CHECK {hub,human,hybrid}（存在） | 零 taskrun 则三个 0＝零态 |
   *
   * **零态与无源必须分开**（口径一、ADR-0047）：这六项**没有一项是无源**——
   * 表和视图都在库里。C-159 回复说「六个字段库里一个都没有」是**核错了**，
   * 三项是真值、三项是零态。 */

  /** 须审批。`null` = 这张卡还没有版本（`current_version_id` 为空）。 */
  hotlGate: z.boolean().nullable(),
  /** 触发方式：schedule / event / manual / undetermined。`null` = 无版本。 */
  triggerType: z.string().nullable(),
  /**
   * 一句话说明（原型的 `desc`）。**由 `v_atc_one_liner` 拼**，不是新字段：
   * 「代理<输入源>，执行<主序列>，生成<产出物>」。`null` = 拼不出来。
   */
  oneLiner: z.string().nullable(),
  /** 拼不出一句话时**缺的是哪几样**。照实显示这个洞，不用看起来合理的话填上。 */
  oneLinerMissing: z.array(z.string()),
  /** 均耗（分钟）。源 `v_atc_avg_duration`；`null` = 还没有成功的执行记录。 */
  avgMinutes: z.number().nullable(),
  /** 执行主体构成。三个都是 0 = 还没有执行记录（零态），不是「没有这个概念」。 */
  actorMix: z.object({ hub: z.number(), human: z.number(), hybrid: z.number() }),
});
export type ExecAtcSummary = z.infer<typeof execAtcSummarySchema>;

export const execAtcListSchema = z.object({ rows: z.array(execAtcSummarySchema) });

export const criteriaRefSchema = z.object({
  label: z.string(),
  repoPath: z.string(),
  bearing: z.string(),
});

export const atcStandardSchema = z.object({
  code: z.string(),
  name: z.string(),
  ownerScope: z.string(),
  determinism: z.string(),
  status: z.string(),
  maturityLevel: z.string(),
  hotlGate: z.boolean(),
  triggerType: z.string(),
  triggerSpec: z.unknown(),
  positionName: z.string().nullable(),
  departmentName: z.string().nullable(),
  dataDomain: z.string().nullable(),

  /* ═══════ 蓝图引用两项（C-160 A3 逐项核库后所加） ═══════
   *
   * **要分开两件事**（口径一）：
   *
   * | 事 | 库里 | 态 |
   * |---|---|---|
   * | 这张卡**引用了哪个蓝图** | `atc.blueprint_atc_ref` / `blueprint_version` 两列**在** | 有字段 → 真值 / 零态 |
   * | 蓝图**本体与三级继承** | **无表**（`app` 下没有 blueprint 表） | **无源**，且 V1 范围外（§9） |
   *
   * C-160 A3 预期「蓝图：无源」。**一半对**：蓝图库本体确实无源，
   * 但「引用了哪个蓝图」这两列在库里，客户实例上为空即**零态**。
   * 两者混成一句会让人以为连引用都没处放。 */

  /** 这张卡引用的蓝图 ATC。`null` = 没引用（零态）。 */
  blueprintRef: z.string().nullable(),
  /** 引用的蓝图版本号。`null` = 没引用。 */
  blueprintVersion: z.string().nullable(),
  what: z.string(),
  how: z.string(),
  actionSpec: z.unknown(),
  testSpec: z.unknown(),
  actionSteps: z.array(z.string()),
  /** 卡上声明的产出物（`action_spec.outputs`，ADR-0037 三）。空 = 没声明。 */
  declaredOutputs: z.array(z.string()),
  /** 判据**地址**，不含内容——复述一份就等于多了一份没人维护的判据。 */
  criteria: z.array(criteriaRefSchema),
  /** 仓库里缺的判据。**照实显示这个洞**，不用看起来合理的话填上。 */
  missingCriteria: z.array(
    z.object({ label: z.string(), expectedAt: z.string(), note: z.string() }),
  ),
  observedOutputKinds: z.array(z.string()),
});
export type AtcStandard = z.infer<typeof atcStandardSchema>;

export const atcInputSchema = z.object({
  materialId: z.string(),
  originalName: z.string().nullable(),
  sourcePath: z.string().nullable(),
  carrierKind: z.string(),
  provenance: z.string(),
  mediaType: z.string(),
  byteSize: z.number(),
  contentTime: z.string().nullable(),
  contentTimeSource: z.string().nullable(),
  uploadedAt: z.string(),
  origin: z.string(),
  runRef: z.string().nullable(),
  registeredAt: z.string(),
  registeredBy: z.string(),
  derivedCount: z.number(),
});
export type AtcInput = z.infer<typeof atcInputSchema>;

export const atcInputListSchema = z.object({ rows: z.array(atcInputSchema) });

export const runLedgerRowSchema = z.object({
  runRef: z.string(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  actorType: z.string(),
  actorId: z.string(),
  triggerRef: z.string().nullable(),
  status: z.string(),
  testResult: z.string(),
  testEvidence: z.unknown(),
  approvalRef: z.string().nullable(),
  responsible: z.string().nullable(),
  artifactCount: z.number(),
  artifactVersion: z.number().nullable(),
  keyOutput: z.string().nullable(),
});
export type RunLedgerRow = z.infer<typeof runLedgerRowSchema>;

export const runLedgerSchema = z.object({ rows: z.array(runLedgerRowSchema) });

export const artifactSchema = z.object({
  artifactId: z.string(),
  atcCode: z.string(),
  runRef: z.string(),
  version: z.number(),
  kind: z.string(),
  mediaType: z.string(),
  byteSize: z.number(),
  contentHash: z.string(),
  producedAt: z.string(),
  producedBy: z.string(),
  targets: z.unknown(),
});
export type Artifact = z.infer<typeof artifactSchema>;

export const artifactListSchema = z.object({ rows: z.array(artifactSchema) });

/* ═══════ 逐版实测均耗（C-191） ═══════
 *
 * 每次执行由 taskrun_lock_version 锁定当时那一版（20260816200000 §8），
 * 所以「每一版各跑了多久」可以从台账派生——派生落视图
 * （v_atc_version_avg_duration），不落存储列。
 * 卡页「基线对照」块的分子一侧：分母是基线（改造前人工），分子是这里的逐版实测。
 */

/** 一个版本的实测均耗。 */
export const versionDurationSchema = z.object({
  /** 版本号（`atc_version.version_no`），从 1 起。 */
  versionNo: z.number(),
  /** 这一版计入的执行次数——**只算 success**（理由见视图注释，同 `v_atc_avg_duration`）。 */
  runCount: z.number(),
  /** 这一版的实测均耗（分钟/次）。源视图 `v_atc_version_avg_duration`。 */
  avgMinutes: z.number(),
  /**
   * 相对当前基线省下的比例：正 = 更快，负 = 更慢。
   * `null` = 这张卡还没录基线——没有分母就谈不上「省下多少」，**不是 0**。
   */
  vsBaseline: z.number().nullable(),
});
export type VersionDuration = z.infer<typeof versionDurationSchema>;

export const versionDurationListSchema = z.object({ rows: z.array(versionDurationSchema) });
export type VersionDurationList = z.infer<typeof versionDurationListSchema>;
